import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ClockCounterClockwise,
  Plus,
  PencilSimple,
  Trash,
  SignIn,
  SignOut,
  Download,
  Export,
  Share,
  Briefcase,
  Gavel,
  Users,
  FileText,
  CurrencyGbp,
  CalendarBlank,
  ChatTeardropDots,
  FunnelSimple,
  CaretDown,
  ArrowsClockwise,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/ui/avatar';
import { useAuthStore } from '@/features/auth/store';
import {
  fetchRecentActivity,
  fetchAuditLogSummary,
  type AuditLogEntry,
} from '@/lib/auditLogApi';

type FilterMode = 'all' | 'mine';

interface ActivityLogPanelProps {
  title?: string;
  showSummary?: boolean;
  limit?: number;
  entityTypes?: string[];
  className?: string;
  defaultFilter?: FilterMode;
}

const ACTION_ICON: Record<string, React.ElementType> = {
  create: Plus,
  update: PencilSimple,
  delete: Trash,
  login: SignIn,
  logout: SignOut,
  download: Download,
  export: Export,
  share: Share,
};

// Icon-chip tints: each light tint is paired with a translucent dark-mode fill so the chip never
// renders a light square on a dark surface (the -600 icon colour reads on both).
const ENTITY_ICON: Record<string, { Icon: React.ElementType; color: string }> = {
  Case: { Icon: Briefcase, color: 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/15' },
  Matter: { Icon: Briefcase, color: 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/15' },
  Hearing: { Icon: Gavel, color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/15' },
  Contact: { Icon: Users, color: 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/15' },
  Document: { Icon: FileText, color: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  FeeRecord: { Icon: CurrencyGbp, color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/15' },
  FeeNote: { Icon: CurrencyGbp, color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/15' },
  CalendarEvent: { Icon: CalendarBlank, color: 'text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-500/15' },
  Task: { Icon: ClockCounterClockwise, color: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-500/15' },
  Message: { Icon: ChatTeardropDots, color: 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15' },
  Allocation: { Icon: Briefcase, color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/15' },
  User: { Icon: Users, color: 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/15' },
};

const ACTION_LABEL: Record<string, string> = {
  create: 'created',
  update: 'updated',
  delete: 'deleted',
  login: 'logged in',
  logout: 'logged out',
  download: 'downloaded',
  export: 'exported',
  share: 'shared',
};

const ENTITY_LABEL: Record<string, string> = {
  Case: 'matter',
  Matter: 'matter',
  Hearing: 'hearing',
  Contact: 'contact',
  Document: 'document',
  FeeRecord: 'fee record',
  FeeNote: 'fee note',
  CalendarEvent: 'calendar event',
  Task: 'task',
  Message: 'message',
  Allocation: 'brief',
  User: 'user',
};

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function getEntityRoute(entityType: string | null, entityId: string | null, basePath: string): string | null {
  if (!entityType) return null;

  // Routes with detail pages (need entityId)
  if (entityId) {
    switch (entityType) {
      case 'Case':
      case 'Matter':
        return `${basePath}/matters/${entityId}`;
      case 'Hearing':
        return `${basePath}/hearings/${entityId}`;
      case 'Contact':
        // Barristers use /clients/:id, clerk/admin use /contacts/:id
        return basePath === '/barrister'
          ? `${basePath}/clients/${entityId}`
          : `${basePath}/contacts/${entityId}`;
      case 'Message':
      case 'Thread':
        return `${basePath}/communications?thread=${entityId}`;
      case 'Barrister':
        // Only clerk/admin have barrister detail pages
        return basePath === '/barrister' ? null : `${basePath}/barristers/${entityId}`;
      case 'Judgment':
        return `${basePath}/judgments/${entityId}`;
    }
  }

  // Routes that only have list pages (no detail view)
  switch (entityType) {
    case 'Document':
      return `${basePath}/documents`;
    case 'Task':
      return `${basePath}/tasks`;
    case 'FeeRecord':
    case 'FeeNote':
      return `${basePath}/fees`;
    case 'CalendarEvent':
      // Admin's diary route is /admin/calendar; clerk & barrister use …/diary.
      return basePath === '/admin' ? '/admin/calendar' : `${basePath}/diary`;
    case 'Allocation':
      // Only clerks have a bookings page; admin & barrister land on their dashboard root.
      return basePath === '/clerk' ? `${basePath}/bookings` : basePath;
    case 'User':
      // Only admin has a team/members page
      return basePath === '/admin' ? `${basePath}/settings/users` : null;
  }

  return null;
}

export function ActivityLogPanel({
  title = 'Activity Log',
  showSummary = true,
  limit = 15,
  entityTypes,
  className,
  defaultFilter = 'all',
}: ActivityLogPanelProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [filter, setFilter] = useState<FilterMode>(defaultFilter);
  const [filterOpen, setFilterOpen] = useState(false);

  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : user?.memberType === 'admin' ? '/admin' : '/barrister';

  const { data: activityData, isLoading, refetch } = useQuery({
    queryKey: ['audit-log', 'recent', filter, limit, entityTypes],
    queryFn: () => fetchRecentActivity({ mine: filter === 'mine', limit, entityTypes }),
    refetchInterval: 60_000, // Refresh every minute
    staleTime: 30_000,
  });

  const { data: summaryData } = useQuery({
    queryKey: ['audit-log', 'summary', filter],
    queryFn: () => fetchAuditLogSummary(filter === 'mine'),
    enabled: showSummary,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const logs = activityData?.logs ?? [];

  // Group logs by date
  const groupedLogs = useMemo(() => {
    const groups: { label: string; logs: AuditLogEntry[] }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let currentGroup: { label: string; logs: AuditLogEntry[] } | null = null;

    for (const log of logs) {
      const logDate = new Date(log.createdAt);
      logDate.setHours(0, 0, 0, 0);

      let label: string;
      if (logDate.getTime() === today.getTime()) {
        label = 'Today';
      } else if (logDate.getTime() === yesterday.getTime()) {
        label = 'Yesterday';
      } else {
        label = logDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
      }

      if (!currentGroup || currentGroup.label !== label) {
        currentGroup = { label, logs: [] };
        groups.push(currentGroup);
      }
      currentGroup.logs.push(log);
    }

    return groups;
  }, [logs]);

  const handleLogClick = (log: AuditLogEntry) => {
    const route = getEntityRoute(log.entityType, log.entityId, basePath);
    if (route) navigate(route);
  };

  return (
    <div className={cn('rounded-xl border border-border/60 bg-card', className)}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`${basePath}/activity-log`)}
            className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline"
          >
            View all →
          </button>
          <button
            onClick={() => refetch()}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-foreground"
            title="Refresh"
          >
            <ArrowsClockwise className="h-4 w-4" />
          </button>
          <div className="relative">
            <button
              onClick={() => setFilterOpen((o) => !o)}
              className="flex h-7 items-center gap-1.5 rounded border border-border/60 bg-background px-2 text-xs text-muted-foreground transition hover:border-border hover:text-foreground"
            >
              <FunnelSimple className="h-3.5 w-3.5" />
              <span>{filter === 'all' ? 'All activity' : 'My activity'}</span>
              <CaretDown className="h-3 w-3" />
            </button>
            {filterOpen && (
              <div className="absolute right-0 top-9 z-50 w-36 overflow-hidden rounded-lg border border-border/60 bg-card py-1 shadow-pop">
                <button
                  onClick={() => { setFilter('all'); setFilterOpen(false); }}
                  className={cn(
                    'block w-full px-3 py-1.5 text-left text-xs transition',
                    filter === 'all' ? 'bg-brand-50 text-brand-700 dark:text-brand-300' : 'text-foreground hover:bg-muted'
                  )}
                >
                  All activity
                </button>
                <button
                  onClick={() => { setFilter('mine'); setFilterOpen(false); }}
                  className={cn(
                    'block w-full px-3 py-1.5 text-left text-xs transition',
                    filter === 'mine' ? 'bg-brand-50 text-brand-700 dark:text-brand-300' : 'text-foreground hover:bg-muted'
                  )}
                >
                  My activity
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {showSummary && summaryData && (
        <div className="grid grid-cols-2 gap-px border-b border-border/60 bg-border/60">
          <div className="bg-card px-4 py-3">
            <div className="text-2xl font-bold text-foreground">{summaryData.today}</div>
            <div className="text-xs text-muted-foreground">Today</div>
          </div>
          <div className="bg-card px-4 py-3">
            <div className="text-2xl font-bold text-foreground">{summaryData.thisWeek}</div>
            <div className="text-xs text-muted-foreground">This week</div>
          </div>
        </div>
      )}

      {/* Activity List */}
      <div className="max-h-[420px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            Loading activity...
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ClockCounterClockwise className="mb-2 h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No activity yet</p>
            <p className="mt-1 text-xs text-muted-foreground/70">
              {filter === 'mine' ? 'Your actions will appear here' : 'Chamber activity will appear here'}
            </p>
          </div>
        ) : (
          <div>
            {groupedLogs.map((group) => (
              <div key={group.label}>
                <div className="sticky top-0 z-10 border-b border-border/40 bg-muted/50 px-4 py-1.5 backdrop-blur-sm">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {group.label}
                  </span>
                </div>
                {group.logs.map((log) => {
                  const entityMeta = ENTITY_ICON[log.entityType ?? ''] ?? { Icon: Briefcase, color: 'text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800' };
                  const { Icon, color } = entityMeta;
                  const ActionIcon = ACTION_ICON[log.action] ?? PencilSimple;
                  const actionLabel = ACTION_LABEL[log.action] ?? log.action;
                  const entityLabel = ENTITY_LABEL[log.entityType ?? ''] ?? log.entityType?.toLowerCase() ?? 'item';
                  const actorName = log.actor
                    ? `${log.actor.firstName} ${log.actor.lastName}`
                    : log.actorEmail ?? 'System';
                  const actorInitials = log.actor
                    ? `${log.actor.firstName[0]}${log.actor.lastName[0]}`
                    : 'SY';

                  // Extract a meaningful label from the after data
                  const afterData = log.after as Record<string, unknown> | null;
                  const entityName =
                    afterData?.caseName ??
                    afterData?.caseNumber ??
                    afterData?.name ??
                    afterData?.title ??
                    afterData?.subject ??
                    '';

                  return (
                    <button
                      key={log.id}
                      type="button"
                      onClick={() => handleLogClick(log)}
                      className="group flex w-full items-start gap-3 border-b border-border/40 px-4 py-3 text-left transition last:border-b-0 hover:bg-muted/40"
                    >
                      <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md', color)}>
                        <Icon className="h-4 w-4" weight="duotone" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm leading-snug text-foreground">
                          <span className="font-medium">{actorName}</span>
                          {' '}
                          <span className="text-muted-foreground">{actionLabel}</span>
                          {' '}
                          <span className="text-muted-foreground">a {entityLabel}</span>
                          {entityName && (
                            <>
                              {': '}
                              <span className="font-medium text-foreground">{String(entityName)}</span>
                            </>
                          )}
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-xs text-muted-foreground/70">
                            {relativeTime(log.createdAt)}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            <ActionIcon className="h-3 w-3" />
                            {log.action}
                          </span>
                        </div>
                      </div>
                      <Avatar
                        fallback={actorInitials}
                        src={log.actor?.avatarUrl ?? undefined}
                        size="xs"
                        className="mt-0.5 shrink-0"
                      />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
